import { memo } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Info } from "lucide-react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import type { RentalCreateFormSchema } from "../../rental-create-schema";
import { StepHeading } from "./StepHeading";

interface StepPeriodProps {
  form: UseFormReturn<RentalCreateFormSchema>;
}

/**
 * Etapa 3 — período (retirada e devolução previstas).
 *
 * Vem antes da etapa de peças de propósito: a disponibilidade de cada
 * peça é checada no intervalo `expectedOutAt..expectedInAt`. Definir as
 * datas primeiro evita o footgun de adicionar peça e a checagem ficar
 * defasada.
 */
export const StepPeriod = memo(function StepPeriod({ form }: StepPeriodProps) {
  return (
    <div className="space-y-6">
      <StepHeading
        title="Período do aluguel"
        description="Retirada e devolução previstas."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="expectedOutAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Retirada prevista *</FormLabel>
              <FormControl>
                <Input type="datetime-local" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="expectedInAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Devolução prevista *</FormLabel>
              <FormControl>
                <Input type="datetime-local" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="flex items-start gap-2 rounded-md border border-input bg-muted/40 p-3 text-xs text-muted-foreground">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <p>
          As datas definem o período usado para checar a disponibilidade das
          peças na próxima etapa.
        </p>
      </div>
    </div>
  );
});
